import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const LOGO_URL = 'https://storage.readdy-site.link/project_files/592e1909-dcd7-4362-8690-bb74ab41b6ab/f375b2c6-56db-4985-a2be-b4997b3e6fa9_khalsa-logo.png?v=f29e87db227a305ed53a3978955c97c9';
const PHONE_DISPLAY = '215-321-0305';
const PHONE_HREF = 'tel:+12153210305';

const navLinks = [
  { label: 'Services', href: '/services' },
  { label: 'About', href: '/about' },
  { label: 'Locations', href: '/locations' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleBook = () => {
    if (location.pathname === '/') {
      const el = document.getElementById('booking');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/#booking');
      setTimeout(() => {
        const el = document.getElementById('booking');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#080808]/96 backdrop-blur-lg border-b border-[#d4af37]/10 py-0'
          : 'bg-transparent py-2'
      }`}
    >
      <nav className="px-6 md:px-10 lg:px-16 flex items-center justify-between h-18 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer">
          <img
            src={LOGO_URL}
            alt="Khalsa Healing Arts & Yoga Center"
            className="h-12 w-auto object-contain"
            style={{ filter: scrolled ? 'brightness(1)' : 'brightness(1.05)' }}
          />
        </Link>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-9">
          {navLinks.map((item) => (
            <li key={item.href}>
              <Link
                to={item.href}
                className={`relative text-xs uppercase tracking-[0.15em] transition-colors duration-300 pb-0.5 whitespace-nowrap cursor-pointer ${
                  location.pathname === item.href
                    ? 'text-[#d4af37]'
                    : 'text-[#9a9a9a] hover:text-white'
                }`}
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {item.label}
                {location.pathname === item.href && (
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-[#d4af37]/60" />
                )}
              </Link>
            </li>
          ))}
          <li>
            <a
              href={PHONE_HREF}
              className="flex items-center gap-1.5 text-[#9a9a9a] hover:text-[#d4af37] transition-colors text-xs uppercase tracking-[0.12em] whitespace-nowrap"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm" />
              </div>
              {PHONE_DISPLAY}
            </a>
          </li>
        </ul>

        {/* Desktop CTA */}
        <button
          onClick={handleBook}
          className="hidden md:inline-flex items-center gap-2 bg-[#d4af37] hover:bg-[#c09b2a] text-[#080808] px-7 py-2.5 rounded-full text-xs uppercase tracking-[0.15em] transition-all duration-300 cursor-pointer whitespace-nowrap font-semibold"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Book Session
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-arrow-right-line text-sm" />
          </div>
        </button>

        {/* Mobile: call + hamburger */}
        <div className="md:hidden flex items-center gap-3">
          <a
            href={PHONE_HREF}
            className="w-9 h-9 flex items-center justify-center rounded-full border border-[#d4af37]/30 text-[#d4af37] cursor-pointer"
          >
            <i className="ri-phone-line text-base" />
          </a>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-9 h-9 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-400 ${
          menuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-[#080808]/98 backdrop-blur-lg border-t border-[#d4af37]/10 px-6 py-7 flex flex-col gap-5">
          {navLinks.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`text-sm uppercase tracking-[0.15em] cursor-pointer transition-colors ${
                location.pathname === item.href ? 'text-[#d4af37]' : 'text-[#9a9a9a] hover:text-white'
              }`}
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {item.label}
            </Link>
          ))}
          <div className="h-px bg-[#d4af37]/10 my-1" />
          <button
            onClick={() => { setMenuOpen(false); handleBook(); }}
            className="bg-[#d4af37] hover:bg-[#c09b2a] text-[#080808] px-7 py-3.5 rounded-full text-xs uppercase tracking-[0.15em] font-semibold text-center cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Book Your Session
          </button>
          <a
            href={PHONE_HREF}
            className="text-center text-[#7a7a7a] text-sm"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Call: {PHONE_DISPLAY}
          </a>
        </div>
      </div>
    </header>
  );
}
